import React, { useEffect, useState } from "react";
import { plansApi } from "../../lib/api";
import { Info } from "lucide-react";

// Cache the plan config across renders (invalidated by app reload; simple + effective)
let _cache = null;
let _pending = null;

export const usePlanConfig = () => {
  const [cfg, setCfg] = useState(_cache);
  useEffect(() => {
    if (_cache) return;
    if (!_pending) _pending = plansApi.getConfig().catch(() => ({ plans: [] }));
    _pending.then((r) => { _cache = r; setCfg(r); });
  }, []);
  return cfg;
};

export const invalidatePlanCache = () => { _cache = null; _pending = null; };

// Parse a facility.nmi_plans string like "ESSENTIAL, BASIC" into an uppercased set
const parsePlans = (nmi) => {
  if (!nmi || typeof nmi !== "string") return new Set();
  return new Set(nmi.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean));
};

/**
 * Given a facility's accepted plans and the full tier order, compute:
 *  - accepted: plans directly listed on the facility
 *  - inherited: plans strictly above the lowest accepted tier (get access via the Russian-doll rule)
 *  - excluded: plans below the lowest accepted tier (do NOT get access)
 */
export const computePlanAccess = (facilityPlans, orderedPlanNames) => {
  const accepted = parsePlans(facilityPlans);
  const orders = new Map(orderedPlanNames.map((n, i) => [n, i + 1]));
  const acceptedOrders = [...accepted]
    .map((p) => orders.get(p))
    .filter((n) => Number.isFinite(n));
  const floor = acceptedOrders.length ? Math.min(...acceptedOrders) : null;
  return orderedPlanNames.map((name, i) => {
    const order = i + 1;
    if (accepted.has(name)) return { name, order, kind: "accepted" };
    if (floor != null && order > floor) return { name, order, kind: "inherited" };
    return { name, order, kind: "excluded" };
  });
};

const dotClasses = {
  accepted: "bg-nmi-700 text-white border-nmi-700",
  inherited: "bg-nmi-50 text-nmi-800 border-nmi-300",
  excluded: "bg-slate-100 text-slate-400 border-slate-200 line-through",
};

/**
 * Compact horizontal tier ladder for a single facility.
 *  - Accepted tier(s): filled dark-green
 *  - Inherited (above lowest accepted): light green
 *  - Excluded (below lowest accepted): grey / strikethrough
 */
export const PlanLadder = ({ nmiPlans, size = "sm", className = "", showLegend = false }) => {
  const cfg = usePlanConfig();
  if (!cfg?.plans?.length) return null;
  const ordered = cfg.plans.filter((p) => p.active).map((p) => p.name);
  const tiers = computePlanAccess(nmiPlans, ordered);
  const noneAccepted = tiers.every((t) => t.kind !== "accepted");
  const px = size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]";
  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex flex-wrap gap-1" data-testid="plan-ladder">
        {tiers.map((t) => (
          <span
            key={t.name}
            data-testid={`plan-tier-${t.name.toLowerCase()}-${t.kind}`}
            title={
              t.kind === "accepted" ? `${t.name} — accepted (floor tier)` :
              t.kind === "inherited" ? `${t.name} — inherited access (Russian-doll rule)` :
              `${t.name} — not accepted here`
            }
            className={`inline-flex items-center rounded-full border font-mono font-bold uppercase tracking-wider ${px} ${dotClasses[t.kind]}`}
          >
            {t.name}
          </span>
        ))}
      </div>
      {noneAccepted && (
        <div className="text-[11px] italic text-slate-500">No plans listed on this facility.</div>
      )}
      {showLegend && (
        <div className="flex flex-wrap items-center gap-3 pt-1 text-[10px] text-slate-500">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-nmi-700" /> Accepted
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-nmi-200 border border-nmi-300" /> Inherited access
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-slate-100 border border-slate-300" /> Not accepted
          </span>
          <span className="ml-auto inline-flex items-center gap-1">
            <Info size={10} /> Order set on the <b className="ml-0.5">Plans</b> page
          </span>
        </div>
      )}
    </div>
  );
};
